import { visibleOptionLabel } from '@/lib/catalog/optionLabel';

/**
 * QA-R6 I — 주문 라인 옵션 표기 헬퍼.
 *
 * 신고: 주문 상세/거래명세서의 옵션 칸이 옵션이 있는데도 '-' 로 비어 보인다.
 *
 * 근본(FE 관점): OrderLineItem(generated)에는 `variantLabel`(단일 문자열) 만 있고, 조합/추가
 * 옵션을 구조화로 나르는 `selectedOptions` 필드가 계약에 없다. 그래서 화면이 `variantLabel ?? '-'`
 * 만 렌더했고, BE 가 조합/추가옵션 주문에 variantLabel 을 비워 보내면 '-' 가 된다.
 *
 * 이 헬퍼는 회원 장바구니 패널(UserCartPanel.formatOptions)과 **동일한 규칙**으로 옵션을 만든다:
 *  1) 구조화 `selectedOptions` 가 있으면 그걸 우선 사용("옵션명: 라벨" / 라벨만, ' / ' 결합).
 *  2) 없으면 `variantLabel` 폴백.
 *  3) 둘 다 없으면 null(호출측이 '-' 표기).
 *
 * be2(K) 가 variantLabel 을 채우든 selectedOptions 를 추가하든 **어느 쪽이 와도** 옳게 표기되도록
 * 두 경로를 모두 수용한다. selectedOptions 는 계약에 아직 없을 수 있어 안전 캐스트로 읽는다.
 */

/** 주문 라인의 구조화 선택 옵션(있을 경우). 장바구니 CartSelectedOption 과 동형. */
export interface OrderItemSelectedOption {
  optionName?: string | null;
  value?: string | null;
  label?: string | null;
}

/** 옵션 표기에 필요한 최소 라인 형태(상세/명세서 공용). */
export interface OrderItemOptionSource {
  variantLabel?: string | null;
  selectedOptions?: ReadonlyArray<OrderItemSelectedOption> | null;
}

/** 단일 선택 옵션을 "옵션명: 표시값" 또는 표시값만으로 만든다(표시값 없으면 빈 문자열). */
const formatSelectedOption = (opt: OrderItemSelectedOption): string => {
  const display = (opt.label ?? opt.value ?? '').trim();
  if (!display) return '';
  const name = opt.optionName?.trim();
  return name ? `${name}: ${display}` : display;
};

/**
 * 주문 라인의 옵션 표기 문자열을 만든다. 옵션이 전혀 없으면 null(호출측이 '-' 등으로 표기).
 * 구조화 selectedOptions 우선, 없으면 variantLabel 폴백.
 */
export const formatOrderItemOption = (item: OrderItemOptionSource): string | null => {
  const options = item.selectedOptions ?? [];
  if (options.length > 0) {
    const composed = options.map(formatSelectedOption).filter(Boolean).join(' / ');
    if (composed) return composed;
  }
  // QA-521 — 옵션 없는 상품의 기본 SKU("기본")는 옵션이 아니라 표기하지 않는다.
  return visibleOptionLabel(item.variantLabel);
};
